import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export enum Granularity {
  DAY = 'day',
  MONTH = 'month',
}

export interface EmailReportBucket {
  period: string
  sent: number
  opened: number
  clicked: number
  openRate: number
  clickRate: number
}

export interface EmailReportTotals {
  sent: number
  opened: number
  clicked: number
  openRate: number
  clickRate: number
}

export interface EmailReportTemplateRow {
  templateId: string | null
  name: string
  sent: number
  opened: number
  clicked: number
  openRate: number
  clickRate: number
}

export interface EmailReport {
  granularity: Granularity
  buckets: EmailReportBucket[]
  byTemplate: EmailReportTemplateRow[]
  totals: EmailReportTotals
}

export interface EmailReportParams {
  granularity: Granularity
  start: string
  end: string
  sender?: string
}

const BASE = '/api/v1/insure/internal/email-report'
const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

function fetchEmailReport(params: EmailReportParams): Promise<EmailReport> {
  const query = new URLSearchParams({
    granularity: params.granularity,
    start: params.start,
    end: params.end,
    tz: browserTimezone(),
  })
  if (params.sender) query.set('sender', params.sender)
  return axios.get(`${BASE}?${query.toString()}`).then((r) => toCamelCase(r.data) as EmailReport)
}

export function useEmailReport(params: EmailReportParams) {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_REPORT, params],
    queryFn: () => fetchEmailReport(params),
  })
}

export function useEmailReportSenders() {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_REPORT, 'senders'],
    queryFn: () =>
      axios.get(`${BASE}/senders`).then((r) => (toCamelCase(r.data) as { senders: string[] }).senders),
  })
}
